/**
 * UserService - User Profile Management
 *
 * Handles user profile CRUD operations, settings management,
 * and real-time user profile synchronization.
 *
 * @see WI-014 - User Profile Schema
 * @see CheckerFly_SDD.md Section 6.2 (Canonical Schemas — User)
 */

import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  User,
  UserSettings,
  UserProfileUpdate,
} from '@shared/models/user.models';
import { SecureLogger } from '../utils/logger.util';
import { devLog, devWarn } from '@core';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvent, AnalyticsUserProperty } from '@core/types/analytics.types';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private firestore = inject(FirestoreService);
  private functions = inject(Functions);
  private analytics = inject(AnalyticsService);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  // Track pending requests to prevent race conditions during concurrent calls
  private pendingUserRequests = new Map<string, Promise<User | null>>();

  /**
   * Observable of current user profile
   * Emits null if no user is loaded
   */
  currentUser$ = this.currentUserSubject.asObservable();

  /**
   * Get current user value synchronously
   */
  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Load user profile from Firestore
   * Performance optimization: Check cache first (fastest), then pending requests, then Firestore.
   * Prevents duplicate reads and race conditions during concurrent calls.
   *
   * @param uid - Firebase user ID
   * @param forceRefresh - If true, bypass cache and fetch from Firestore (default: false)
   * @returns Promise resolving to User or null if not found
   */
  async loadUser(uid: string, forceRefresh: boolean = false): Promise<User | null> {
    try {
      // Check cache FIRST (fastest - simple object comparison)
      const cachedUser = this.currentUserSubject.value;
      if (!forceRefresh && cachedUser?.id === uid) {
        devLog('[UserService] Using cached user profile for uid:', uid);
        return cachedUser;
      }

      // Check pending request SECOND (Map lookup - slower than cache check)
      if (!forceRefresh && this.pendingUserRequests.has(uid)) {
        devLog('[UserService] Reusing pending request for uid:', uid);
        const pendingRequest = this.pendingUserRequests.get(uid);
        if (!pendingRequest) {
          throw new Error('Pending request not found after has() check');
        }
        return pendingRequest;
      }

      // Not in cache or force refresh - fetch from Firestore (slowest)
      devLog('[UserService] Loading user profile from Firestore for uid:', uid);

      // Create and store promise to prevent duplicate concurrent requests
      const promise = this.firestore.getDoc<User>(`users/${uid}`)
        .then(user => {
          this.currentUserSubject.next(user);
          // Clean up pending request after completion
          this.pendingUserRequests.delete(uid);
          return user;
        })
        .catch(error => {
          // Clean up pending request on error too
          this.pendingUserRequests.delete(uid);
          throw error;
        });

      this.pendingUserRequests.set(uid, promise);
      return promise;
    } catch (error) {
      SecureLogger.error('UserService', { uid, error });
      throw error;
    }
  }

  /**
   * Create real-time listener for user profile changes
   * @param uid - Firebase user ID
   * @returns Observable that emits User data on changes
   */
  watchUser(uid: string): Observable<User | null> {
    const user$ = this.firestore.docListener<User>(`users/${uid}`);

    // Update currentUserSubject when user data changes
    user$.subscribe((user) => {
      this.currentUserSubject.next(user);
    });

    return user$;
  }

  /**
   * Update user profile fields (rate-limited via Cloud Function)
   * Only allows updating name, email, timezone, and locale.
   * System fields (id, createdAt, etc.) cannot be updated.
   *
   * Rate limit: 10 updates per hour
   *
   * @param uid - Firebase user ID
   * @param updates - Partial user profile with fields to update
   * @throws Error if rate limit exceeded
   */
  async updateProfile(
    uid: string,
    updates: UserProfileUpdate
  ): Promise<void> {
    if (!uid) {
      throw new Error('User ID required');
    }

    // Client-side validation (before Cloud Function call)
    this.validateProfileUpdate(updates);

    try {
      // Call rate-limited Cloud Function
      const callable = httpsCallable<
        { updates: Partial<User> },
        { success: boolean; message: string }
      >(this.functions, 'updateUserProfile');

      await callable({ updates });

      // Reload user data to reflect changes
      // Force refresh to bypass cache and get updated data
      await this.loadUser(uid, true);

      // WI-039: Set user properties after update
      const currentUser = this.currentUserSubject.value;
      if (currentUser) {
        this.analytics.setUserProperties({
          [AnalyticsUserProperty.SUBSCRIPTION_TIER]: currentUser.subscriptionTier,
          [AnalyticsUserProperty.TIMEZONE]: currentUser.timezone,
        });
      }
    } catch (error: unknown) {
      // Type-safe error handling with type guard
      const hasCode = typeof error === 'object' && error !== null && 'code' in error;
      const hasMessage = typeof error === 'object' && error !== null && 'message' in error;

      // Handle rate limit error
      if (hasCode && (error as { code: unknown }).code === 'functions/resource-exhausted') {
        SecureLogger.error('UserService', { uid, error });
        const message = hasMessage ? String((error as { message: unknown }).message) : 'Rate limit exceeded';
        throw new Error(message); // Pass rate limit message to UI
      }

      SecureLogger.error('UserService', { uid, error });
      throw error;
    }
  }

  /**
   * Update user settings (rate-limited via Cloud Function)
   * Performs deep merge to handle nested objects correctly (e.g., quietHours).
   *
   * Rate limit: 20 updates per hour
   *
   * @param uid - Firebase user ID
   * @param settings - Partial settings object with fields to update
   * @throws Error if rate limit exceeded
   *
   * @example
   * // Only update quietHours.enabled, preserve start/end
   * await updateSettings('uid', {
   *   quietHours: { enabled: false }
   * });
   */
  async updateSettings(
    uid: string,
    settings: Partial<UserSettings>
  ): Promise<void> {
    if (!uid) {
      throw new Error('User ID required');
    }

    if (!this.currentUserSubject.value) {
      throw new Error('No current user loaded. Call loadUser() first.');
    }

    // Client-side validation (before Cloud Function call)
    this.validateSettings(settings);

    try {
      // Call rate-limited Cloud Function
      const callable = httpsCallable<
        { settings: Partial<UserSettings> },
        { success: boolean; message: string }
      >(this.functions, 'updateUserSettings');

      await callable({ settings });

      // Reload user data to reflect changes
      // Force refresh to bypass cache and get updated data
      await this.loadUser(uid, true);
    } catch (error: unknown) {
      // Type-safe error handling with type guard
      const hasCode = typeof error === 'object' && error !== null && 'code' in error;
      const hasMessage = typeof error === 'object' && error !== null && 'message' in error;

      // Handle rate limit error
      if (hasCode && (error as { code: unknown }).code === 'functions/resource-exhausted') {
        SecureLogger.error('UserService', { uid, error });
        const message = hasMessage ? String((error as { message: unknown }).message) : 'Rate limit exceeded';
        throw new Error(message); // Pass rate limit message to UI
      }

      SecureLogger.error('UserService', { uid, error });
      throw error;
    }
  }

  /**
   * Mark user onboarding as complete
   *
   * Phase 1: Direct Firestore write (security rules allow authenticated users to update their own onboardingComplete)
   * Phase 3+: Will migrate to Cloud Function for rate limiting and validation
   *
   * @param uid - Firebase user ID
   */
  async completeOnboarding(uid: string): Promise<void> {
    try {
      // Phase 1: Direct Firestore write
      // Security rules allow: allow update: if request.auth.uid == userId && request.resource.data.diff(resource.data).affectedKeys().hasOnly(['onboardingComplete'])
      await this.firestore.updateDoc(`users/${uid}`, {
        onboardingComplete: true
      });

      // Reload user to ensure currentUserSubject is in sync
      // IMPORTANT: Force refresh to bypass cache and get updated onboardingComplete flag
      await this.loadUser(uid, true);

      // WI-039: Track onboarding completion
      const currentUser = this.currentUserSubject.value;
      if (currentUser) {
        this.analytics.logEvent(AnalyticsEvent.ONBOARDING_PROFILE_COMPLETED, {
          has_display_name: !!currentUser.name,
          timezone: currentUser.timezone,
          locale: currentUser.locale,
        });

        // Set user properties for segmentation
        this.analytics.setUserProperties({
          [AnalyticsUserProperty.SUBSCRIPTION_TIER]: currentUser.subscriptionTier,
          [AnalyticsUserProperty.ONBOARDING_COMPLETE]: true,
          [AnalyticsUserProperty.TIMEZONE]: currentUser.timezone,
        });
      }
    } catch (error) {
      SecureLogger.error('UserService', { uid, error });
      throw error;
    }
  }

  /**
   * Check if user document exists in Firestore
   * @param uid - Firebase user ID
   * @returns Promise resolving to true if user exists, false otherwise
   */
  async userExists(uid: string): Promise<boolean> {
    try {
      const user = await this.firestore.getDoc<User>(`users/${uid}`);
      return user !== null;
    } catch (error) {
      SecureLogger.error('UserService', { uid, error });
      return false;
    }
  }

  /**
   * Wait for user document to be created by Cloud Function
   *
   * After successful OTP verification, Firebase Auth triggers the createUserProfile
   * Cloud Function which creates the user document. This can take 500ms-2s normally,
   * but up to 15s on first run due to cold start.
   *
   * This method polls Firestore until the user document exists or timeout occurs.
   *
   * Retry strategy optimized for cold starts:
   * - Fast initial retries (100ms, 200ms) for warm functions
   * - Slower later retries (up to 2s) for cold starts
   * - Max total wait time: ~20 seconds
   *
   * @param uid - Firebase user ID
   * @param maxAttempts - Maximum number of retry attempts (default: 12)
   * @returns Promise resolving to User when document is created
   * @throws Error if user document not created within timeout
   */
  async waitForUserCreation(
    uid: string,
    maxAttempts: number = 12
  ): Promise<User> {
    devLog('[UserService] Waiting for user document to be created by Cloud Function', { uid });

    // Optimized retry delays for cold starts:
    // [0, 100, 200, 500, 1000, 2000, 2000, 2000, 2000, 2000, 2000, 2000]
    // Total: ~16 seconds of delays + function execution time
    const retryDelays = [
      0,    // Attempt 1: immediate
      100,  // Attempt 2: 100ms (fast check for warm function)
      200,  // Attempt 3: 200ms
      500,  // Attempt 4: 500ms
      1000, // Attempt 5: 1s
      2000, // Attempt 6: 2s
      2000, // Attempt 7: 2s (for cold starts)
      2000, // Attempt 8: 2s
      2000, // Attempt 9: 2s
      2000, // Attempt 10: 2s
      2000, // Attempt 11: 2s
      2000  // Attempt 12: 2s
    ];

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const user = await this.firestore.getDoc<User>(`users/${uid}`);

        if (user) {
          devLog('[UserService] User document found', { uid, attempt });
          // Update cache
          this.currentUserSubject.next(user);
          return user;
        }

        // User document doesn't exist yet, wait before retry
        if (attempt < maxAttempts) {
          const nextDelay = retryDelays[attempt] || 2000;
          devLog('[UserService] User document not found, retrying...', {
            uid,
            attempt,
            nextRetryIn: nextDelay
          });

          await new Promise(resolve => setTimeout(resolve, nextDelay));
        }
      } catch (error) {
        devWarn('[UserService] Error checking for user document', { uid, attempt, error });

        // If this is the last attempt, throw the error
        if (attempt === maxAttempts) {
          throw new Error(
            `User document not created after ${maxAttempts} attempts. ` +
            `This usually means the Cloud Function failed to execute. ` +
            `Check the emulator logs for errors.`
          );
        }

        // Otherwise, wait and retry
        const nextDelay = retryDelays[attempt] || 2000;
        await new Promise(resolve => setTimeout(resolve, nextDelay));
      }
    }

    // Should never reach here due to throw in last attempt, but TypeScript needs this
    throw new Error(`User document not created after ${maxAttempts} attempts`);
  }

  /**
   * Clear current user state
   * Call this on logout.
   */
  clearCurrentUser(): void {
    this.currentUserSubject.next(null);
  }

  /**
   * Get rate limit status for UI display
   *
   * **WI-028**: Type-safe Cloud Function response handling
   *
   * @returns Rate limit info for profile and settings updates
   */
  async getRateLimitInfo(): Promise<{
    profileUpdates: { remaining: number; resetAt: Date; limit: number };
    settingsUpdates: { remaining: number; resetAt: Date; limit: number };
  }> {
    try {
      const callable = httpsCallable<
        Record<string, never>,
        {
          profileUpdates: { remaining: number; resetAt: Date; limit: number };
          settingsUpdates: { remaining: number; resetAt: Date; limit: number };
        }
      >(this.functions, 'getRateLimitInfo');
      const result = await callable({});
      return result.data;
    } catch (error) {
      SecureLogger.error('UserService', { error });
      throw new Error('Failed to get rate limit info');
    }
  }

  /**
   * Deep merge two objects recursively
   *
   * SECURITY: Prevents prototype pollution by:
   * - Using Object.prototype.hasOwnProperty.call() instead of hasOwnProperty()
   * - Blocking __proto__, constructor, and prototype keys
   * - Creating new objects instead of mutating existing ones
   *
   * @param target - Target object
   * @param source - Source object to merge
   * @returns Merged object
   */
  private deepMerge<T>(target: T, source: Partial<T>): T {
    const result = { ...target };

    for (const key in source) {
      // SECURITY FIX: Use safe hasOwnProperty check
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        // SECURITY FIX: Prevent __proto__ pollution
        // H-02: Use dev-logger for production PII protection
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          devWarn('[UserService] Blocked attempt to modify prototype chain:', key);
          continue;
        }

        const sourceValue = source[key];
        const targetValue = result[key];

        if (
          sourceValue &&
          typeof sourceValue === 'object' &&
          !Array.isArray(sourceValue) &&
          targetValue &&
          typeof targetValue === 'object' &&
          !Array.isArray(targetValue)
        ) {
          result[key] = this.deepMerge(targetValue, sourceValue);
        } else {
          // WI-028: Type-safe assignment - sourceValue is already Partial<T>[keyof T]
          result[key] = sourceValue as T[Extract<keyof T, string>];
        }
      }
    }

    return result;
  }

  /**
   * Validates user profile update fields
   *
   * @param updates - Profile fields to validate
   * @throws Error if validation fails
   */
  private validateProfileUpdate(updates: Partial<User>): void {
    // Name validation
    if (updates.name !== undefined) {
      if (!updates.name || updates.name.trim().length === 0) {
        throw new Error('Name cannot be empty');
      }
      if (updates.name.length > 100) {
        throw new Error('Name must be 100 characters or less');
      }
      // Prevent HTML/script injection
      if (/<[^>]*>/g.test(updates.name)) {
        throw new Error('Name cannot contain HTML tags');
      }
    }

    // Email validation
    if (updates.email !== undefined && updates.email !== null) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(updates.email)) {
        throw new Error('Invalid email format');
      }
      if (updates.email.length > 254) {
        throw new Error('Email too long');
      }
    }

    // Timezone validation
    if (updates.timezone !== undefined) {
      try {
        const validTimezones = Intl.supportedValuesOf('timeZone');
        if (!validTimezones.includes(updates.timezone)) {
          throw new Error('Invalid IANA timezone');
        }
      } catch (e) {
        throw new Error('Invalid timezone format');
      }
    }

    // Locale validation
    if (updates.locale !== undefined) {
      const localeRegex = /^[a-z]{2}-[A-Z]{2}$/;
      if (!localeRegex.test(updates.locale)) {
        throw new Error('Invalid locale format (expected: en-US)');
      }
    }
  }

  /**
   * Validates user settings values
   *
   * @param settings - Settings to validate
   * @throws Error if validation fails
   */
  private validateSettings(settings: Partial<UserSettings>): void {
    // Grace window validation (15-120 minutes)
    if (settings.defaultGraceWindow !== undefined) {
      if (settings.defaultGraceWindow < 15 || settings.defaultGraceWindow > 120) {
        throw new Error('Grace window must be between 15 and 120 minutes');
      }
    }

    // Nudge delay validation (5-60 minutes)
    if (settings.defaultNudgeDelay !== undefined) {
      if (settings.defaultNudgeDelay < 5 || settings.defaultNudgeDelay > 60) {
        throw new Error('Nudge delay must be between 5 and 60 minutes');
      }
    }

    // Quiet hours time format validation
    if (settings.quietHours) {
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

      if (settings.quietHours.start && !timeRegex.test(settings.quietHours.start)) {
        throw new Error('Invalid quiet hours start time format (expected: HH:MM)');
      }

      if (settings.quietHours.end && !timeRegex.test(settings.quietHours.end)) {
        throw new Error('Invalid quiet hours end time format (expected: HH:MM)');
      }
    }
  }
}
